import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { BedDouble, Clock, FlaskConical, Pill, RefreshCw, Building2 } from 'lucide-react';
import { formatCATDateTime } from '../../utils/timezone';
import { deptLabel } from './VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (symbol, n) => `${symbol}${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Live board of admitted patients per hospital: days admitted + running cost so far.
export default function WardBoardPanel({ visits, authCfg, isSuperAdmin, onOpenPatient }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const admittedCount = (visits || []).filter((v) => v.status === 'ADMITTED').length;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inpatient/ward-board`, authCfg);
      setBoard(r.data);
    } catch (e) {
      setBoard({ hospitals: [], error: e.response?.data?.detail || 'Failed to load ward board' });
    } finally {
      setLoading(false);
    }
  }, [authCfg]);

  // Reload whenever the live queue changes (WebSocket refresh) and every 60s for the day counter
  useEffect(() => { load(); }, [load, admittedCount, visits]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (loading) return <div className="text-center py-10 text-gray-500">Loading ward board…</div>;
  const hospitals = board?.hospitals || [];

  return (
    <div className="space-y-4" data-testid="ward-board">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><BedDouble className="w-5 h-5 text-rose-600" /> Ward Board — {board?.total_patients || 0} admitted</h2>
        <div className="flex items-center gap-3 text-sm text-gray-600">
          {isSuperAdmin && <span data-testid="ward-board-total-usd">Running cost (all hospitals): <strong className="text-gray-900">${Number(board?.total_usd || 0).toFixed(2)} USD</strong></span>}
          <button onClick={load} className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline" data-testid="ward-board-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>
      {board?.error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{board.error}</div>}
      {hospitals.length === 0 && !board?.error && (
        <Card><CardContent className="py-10 text-center text-gray-500" data-testid="ward-board-empty">No patients are currently admitted.</CardContent></Card>
      )}
      {hospitals.map((h) => (
        <Card key={h.hospital_name} className="border-rose-200" data-testid={`ward-board-hospital-${h.hospital_name.replace(/\s+/g, '-')}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-500" /> {h.hospital_name} <span className="text-sm font-normal text-gray-500">({h.patients.length} patient{h.patients.length > 1 ? 's' : ''} • {h.bed_days} bed day{h.bed_days > 1 ? 's' : ''})</span></span>
              <span className="text-sm font-semibold text-rose-700" data-testid={`ward-board-hospital-total-${h.hospital_name.replace(/\s+/g, '-')}`}>{fmt(h.currency_symbol, h.total_local)} {h.currency_code}{h.currency_code !== 'USD' ? ` (≈ $${Number(h.total_usd).toFixed(2)})` : ''}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                    <th className="p-2">Patient</th><th className="p-2">Ward / Admission</th><th className="p-2">Doctor</th><th className="p-2 text-center">Days</th><th className="p-2 text-center">Pending</th><th className="p-2">Cost so far</th><th className="p-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {h.patients.map((p) => (
                    <tr key={p.visit_id} className="border-b border-gray-100 hover:bg-rose-50/40 cursor-pointer" onClick={() => onOpenPatient && onOpenPatient(p.visit_id)} data-testid={`ward-board-row-${p.visit_id}`}>
                      <td className="p-2 align-top">
                        <div className="font-semibold text-gray-900">{p.patient_name}</div>
                        <div className="text-xs text-gray-500">{p.patient_serial_number} • {p.visit_id}</div>
                        {p.reason && <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{p.reason}</div>}
                      </td>
                      <td className="p-2 align-top">
                        <div className="text-gray-800">{p.ward || <span className="text-gray-400">No ward set</span>}</div>
                        <div className="text-xs font-mono text-gray-500">{p.admission_number}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {formatCATDateTime(p.admitted_at)}</div>
                      </td>
                      <td className="p-2 align-top text-gray-800">{p.doctor}<div className="text-xs text-gray-500">{deptLabel(p.department)} • {p.rounds} round{p.rounds === 1 ? '' : 's'}</div></td>
                      <td className="p-2 align-top text-center"><span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full font-bold ${p.days >= 7 ? 'bg-red-100 text-red-700' : p.days >= 3 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`ward-board-days-${p.visit_id}`}>{p.days}</span></td>
                      <td className="p-2 align-top text-center text-xs">
                        {p.pending_tests > 0 && <span className="inline-flex items-center gap-1 text-blue-700 mr-2"><FlaskConical className="w-3 h-3" /> {p.pending_tests} test{p.pending_tests > 1 ? 's' : ''}</span>}
                        {p.pending_rx > 0 && <span className="inline-flex items-center gap-1 text-purple-700"><Pill className="w-3 h-3" /> {p.pending_rx} rx</span>}
                        {p.pending_tests === 0 && p.pending_rx === 0 && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="p-2 align-top text-xs text-gray-600">
                        <div>Tests {fmt(p.currency_symbol, p.cost.tests)}</div>
                        <div>Medicines {fmt(p.currency_symbol, p.cost.medicines)}</div>
                        <div>Services {fmt(p.currency_symbol, p.cost.services)}</div>
                        {p.cost.bed > 0 && <div>Bed ({p.days}d) {fmt(p.currency_symbol, p.cost.bed)}</div>}
                      </td>
                      <td className="p-2 align-top text-right font-bold text-gray-900 whitespace-nowrap" data-testid={`ward-board-total-${p.visit_id}`}>{fmt(p.currency_symbol, p.cost.total_local)}{p.currency_code !== 'USD' && <div className="text-xs font-normal text-gray-500">≈ ${Number(p.cost.total_usd).toFixed(2)}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
      <p className="text-xs text-gray-400">Running cost = completed tests + medicines + services + bed charge to date. The final bill is generated at discharge.</p>
    </div>
  );
}
